import {
  DEAL_PAYROLL_COEFFICIENT_MODE, DealPayrollCoefficientUnit, DealPayrollGrade,
  DealPayrollGroupSettings
} from 'app/models/deal-payroll.model';
import {
  dealPayrollGroupSettingsProblemKey, dealPayrollTeamCoefficientUnits,
  isDealPayrollGroupSettingsSaveable
} from 'app/models/deal-payroll-settings.adapter';
import {dealPayrollGradeKey} from 'app/models/deal-payroll-presentation';
import {FOUR_DECIMAL_SCALE_FACTOR, MAXIMUM_COEFFICIENT_VALUE,
  MAXIMUM_HOURLY_RATE_VALUE} from 'app/models/deal-payroll-settings.constants';
import {
  DEAL_PAYROLL_AREA_CATALOG_STATE_KEY, DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY,
  DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY
} from './deal-payroll-group-settings.constants';
import {
  DealPayrollAreaCatalogState, DealPayrollGroupSettingsStatus,
  DealPayrollGroupSettingsViewState
} from './deal-payroll-group-settings.types';

const NUMBER_TYPE = 'number';
const ZERO = 0;
const HALF_UP_DIVISOR = 2;

export class DealPayrollGroupSettingsViewModel implements DealPayrollGroupSettingsViewState {
  settings: DealPayrollGroupSettings = null;
  blockerKeys: string[] = [];
  gradeLabels: {[gradeCode: string]: string} = {};
  loading = false;
  saving = false;
  areaCatalogRefreshing = false;
  errorKey = DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.NONE;
  successKey = DEAL_PAYROLL_GROUP_SETTINGS_FEEDBACK_KEY.NONE;
  canRestoreConflictDraft = false;

  constructor(
    private hasConflictDraft: () => boolean,
    private isDraftPristine: () => boolean
  ) {}

  get gradeMonthlySalaries(): {[code: string]: number | null} {
    const values: {[code: string]: number | null} = {};
    (this.settings && this.settings.grades || []).forEach(grade => {
      values[grade.gradeCode] = previewGradeMonthlySalary(this.settings.baseMonthlySalary, grade.coefficient);
    });
    return values;
  }
  get canSave(): boolean {
    return !isDealPayrollGroupSettingsBusy(this) && !this.hasConflictDraft()
      && isDealPayrollGroupSettingsSaveable(this.settings);
  }
  get editingDisabled(): boolean {
    return isDealPayrollGroupSettingsBusy(this) || !this.settings
      || this.settings.canManage !== true || this.hasConflictDraft();
  }
  get saveDisabledReasonKey(): string {
    return this.settings && !isDealPayrollGroupSettingsBusy(this)
      ? dealPayrollGroupSettingsProblemKey(this.settings)
      : DEAL_PAYROLL_GROUP_SETTINGS_ERROR_KEY.NONE;
  }
  get showAreaSettings(): boolean {
    return this.hasMode(DEAL_PAYROLL_COEFFICIENT_MODE.AREA); }
  get showTeamSettings(): boolean {
    return this.hasMode(DEAL_PAYROLL_COEFFICIENT_MODE.TEAM); }
  get showTaskGradeSettings(): boolean { return this.hasMode(DEAL_PAYROLL_COEFFICIENT_MODE.TASK_GRADE); }
  get taskGradeCoefficientUnits(): DealPayrollCoefficientUnit[] {
    return (this.settings && this.settings.coefficientUnits || []).filter(unit => !!unit && unit.type === DEAL_PAYROLL_COEFFICIENT_MODE.TASK_GRADE);
  }
  get showNoCoefficientSettings(): boolean {
    return this.hasMode(DEAL_PAYROLL_COEFFICIENT_MODE.NONE); }
  get teamCoefficientUnits(): DealPayrollCoefficientUnit[] {
    return dealPayrollTeamCoefficientUnits(this.settings); }
  get areaCatalogUnavailableWithStoredAreas(): boolean {
    return this.areaCatalog.unavailableWithStoredAreas; }
  get areaCatalogUnavailableWithoutAreas(): boolean {
    return this.areaCatalog.unavailableWithoutAreas; }
  get areaCatalogAvailableButEmpty(): boolean {
    return this.areaCatalog.availableButEmpty; }
  get areaCatalogStateKey(): string { return this.areaCatalog.stateKey; }
  get canRetryAreaCatalog(): boolean {
    const catalog = this.areaCatalog;
    return (catalog.unavailableWithStoredAreas || catalog.unavailableWithoutAreas)
      && !isDealPayrollGroupSettingsBusy(this) && this.isDraftPristine();
  }
  get areaCatalogRetryReasonKey(): string {
    const catalog = this.areaCatalog;
    const applicable = catalog.unavailableWithStoredAreas || catalog.unavailableWithoutAreas;
    return applicable && !this.areaCatalogRefreshing && !this.isDraftPristine()
      ? DEAL_PAYROLL_AREA_CATALOG_STATE_KEY.RETRY_DIRTY
      : DEAL_PAYROLL_AREA_CATALOG_STATE_KEY.NONE;
  }
  get areaCatalogRefreshKey(): string {
    return this.areaCatalogRefreshing ? DEAL_PAYROLL_AREA_CATALOG_STATE_KEY.REFRESHING
      : DEAL_PAYROLL_AREA_CATALOG_STATE_KEY.NONE;
  }
  get canRetryLoad(): boolean {
    return !isDealPayrollGroupSettingsBusy(this) && (this.hasConflictDraft()
      || !this.settings || this.isDraftPristine());
  }

  private get areaCatalog(): DealPayrollAreaCatalogState {
    return dealPayrollAreaCatalogState(this.settings); }
  private hasMode(mode: string): boolean {
    return !!this.settings && this.settings.coefficientMode === mode; }
}

export function isDealPayrollGroupSettingsBusy(status: DealPayrollGroupSettingsStatus): boolean {
  return status.loading || status.saving || status.areaCatalogRefreshing;
}

export function dealPayrollAreaCatalogState(
  settings: DealPayrollGroupSettings
): DealPayrollAreaCatalogState {
  const areaMode = !!settings && settings.coefficientMode === DEAL_PAYROLL_COEFFICIENT_MODE.AREA;
  const hasAreas = areaMode && !!settings.areas.length;
  const available = areaMode && settings.areaCatalogAvailable === true;
  const unavailableWithStoredAreas = areaMode && !available && hasAreas;
  const unavailableWithoutAreas = areaMode && !available && !hasAreas;
  const availableButEmpty = available && !hasAreas;
  return {unavailableWithStoredAreas: unavailableWithStoredAreas,
    unavailableWithoutAreas: unavailableWithoutAreas, availableButEmpty: availableButEmpty,
    stateKey: areaCatalogKey(unavailableWithStoredAreas, unavailableWithoutAreas, availableButEmpty)};
}

export function dealPayrollGroupGradeLabels(
  settings: DealPayrollGroupSettings
): {[gradeCode: string]: string} {
  const labels: {[gradeCode: string]: string} = {};
  (settings.grades || []).forEach((grade: DealPayrollGrade) => {
    labels[grade.gradeCode] = dealPayrollGradeKey(grade.gradeCode);
  });
  return labels;
}

function areaCatalogKey(stored: boolean, empty: boolean, availableEmpty: boolean): string {
  if (stored) { return DEAL_PAYROLL_AREA_CATALOG_STATE_KEY.UNAVAILABLE_STORED; }
  if (empty) { return DEAL_PAYROLL_AREA_CATALOG_STATE_KEY.UNAVAILABLE_EMPTY; }
  return availableEmpty ? DEAL_PAYROLL_AREA_CATALOG_STATE_KEY.AVAILABLE_EMPTY
    : DEAL_PAYROLL_AREA_CATALOG_STATE_KEY.NONE;
}

export function previewGradeMonthlySalary(base: number, coefficient: number): number | null {
  if (typeof base !== NUMBER_TYPE || !isFinite(base) || base <= ZERO || base > MAXIMUM_HOURLY_RATE_VALUE
    || typeof coefficient !== NUMBER_TYPE || !isFinite(coefficient) || coefficient <= ZERO
    || coefficient > MAXIMUM_COEFFICIENT_VALUE) { return null; }
  const scale = FOUR_DECIMAL_SCALE_FACTOR;
  const baseUnits = Math.round(base * scale);
  const coefficientUnits = Math.round(coefficient * scale);
  // Split before multiplying: each integer product stays exact within the existing input bounds.
  const wholeProduct = Math.floor(baseUnits / scale) * coefficientUnits;
  const remainder = (wholeProduct % scale) * scale + (baseUnits % scale) * coefficientUnits;
  const productScale = scale * scale;
  return Math.floor(wholeProduct / scale) + Math.floor((remainder + productScale / HALF_UP_DIVISOR) / productScale);
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/deal-payroll-group-settings/deal-payroll-group-settings.selectors.ts